import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { useNavigate } from "react-router-dom";
import { urlEndPoint } from "helpers/GeneralHelper";

import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Row,
} from "reactstrap";
import TableContainer from "components/Common/TableContainer";
import { submitConversationTemplateFilterData } from "model/ConversationTemplateModel";
import { useConversationTemplateContext } from "libs/context/ConversationTemplateContext";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";

const ConversationTemplate = () => {
    const { permissionData } = usePermissionContext() || {};
    const navigate = useNavigate();
    const {
        fetchConversationTemplateContextData,
        conversationTemplateContextData,
    } = useConversationTemplateContext() || {};

    const [searchQuery, setSearchQuery] = useState("");
    const [conversationTemplateData, setConversationTemplateData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tableMetaData, setTableMetadata] = useState();
    const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);

    const filteredData = useMemo(() => {
        if (!searchQuery?.trim()) return conversationTemplateData;

        return conversationTemplateData?.filter((item) =>
            Object.values(item).some((val) =>
                String(val).toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
    }, [conversationTemplateData]);

    const conversationTemplateActionPermissions = useMemo(
        () => ({
            canCreate: Boolean(permissionData?.ConversationTemplateAddAccess),
            canEdit: Boolean(permissionData?.ConversationTemplateUpdateAccess),
        }),
        [permissionData]
    );

    const hasAnyConversationTemplateRowAction = useMemo(() => {
        const { canCreate, ...rowActions } = conversationTemplateActionPermissions;
        return Object.values(rowActions).some(Boolean);
    }, [conversationTemplateActionPermissions]);

    useEffect(() => {
        fetchConversationTemplateContextData();
        getConversationTemplateList();
    }, []);

    useEffect(() => {
        const valueReModified = conversationTemplateContextData?.table?.columns?.map((col) => {
            const field = col?.field;
            const normalizedField = String(field ?? "").trim().toLowerCase();
            const isStatusColumn =
                normalizedField === "status" ||
                normalizedField === "statusid" ||
                String(col?.type ?? "").trim().toLowerCase() === "status";

            return {
                Header: col?.title,
                accessor: isStatusColumn ? "statusId" : field,
                type: col?.type,
            };
        });
        setTableMetadata(valueReModified);
    }, [conversationTemplateContextData]);

    const getConversationTemplateList = async (payload = {}) => {
        setLoading(true);
        const response = await submitConversationTemplateFilterData(payload);
        const responseData = response?.data || {};
        const listData = responseData?.records || responseData || [];

        if (response) {
            setConversationTemplateData(Array.isArray(listData) ? listData : []);
        } else {
            setConversationTemplateData([]);
        }
        setLoading(false);
    };

    const handleFilterSubmit = async (data) => {
        await getConversationTemplateList(data);
        setCommonFilterModalOpen(false);
    };

    const handleSearchSubmit = async (value) => {
        await getConversationTemplateList({ nameContains: value?.trim() || "" });
    };

    const handleEdit = (row) => {
        const templateId = row?.id ?? row?.Id;
        if (!templateId) return;
        const templateName = row?.name ?? row?.Name ?? row?.templateName ?? "";
        navigate(`/${urlEndPoint.addEditConversation}/${templateId}`, {
            state: { templateName },
        });
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="fw-bold mb-3">Conversation Template</h5>
                                {conversationTemplateActionPermissions.canCreate ? (
                                    <Col xs="12" md="4" className="text-md-end mt-2 mt-md-0">
                                        <Button
                                            color="primary"
                                            onClick={() =>
                                                navigate(`/${urlEndPoint.addEditConversation}`)
                                            }
                                        >
                                            + New Template
                                        </Button>
                                    </Col>
                                ) : null}
                            </div>
                            <hr />

                            <Row className="align-items-center g-2 mb-4">
                                <Col xs="12" md="8">
                                    <div className="d-flex border rounded w-100 category-search-input">
                                        <Input
                                            type="text"
                                            placeholder="Search by name"
                                            className="border-0"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e?.target?.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    handleSearchSubmit(searchQuery?.trim());
                                                }
                                            }}
                                        />
                                        <Button
                                            className="search-icon-button"
                                            onClick={() => handleSearchSubmit(searchQuery?.trim())}
                                        >
                                            <i className="fa fa-search"></i>
                                        </Button>
                                    </div>
                                </Col>
                            </Row>

                            {!loading && tableMetaData?.length ? (
                                <TableContainer
                                    columns={tableMetaData}
                                    data={filteredData}
                                    isActionRequired={hasAnyConversationTemplateRowAction}
                                    isEditAccess={conversationTemplateActionPermissions.canEdit}
                                    onEdit={
                                        conversationTemplateActionPermissions.canEdit
                                            ? handleEdit
                                            : undefined
                                    }
                                    isCheckboxLeftSide={false}
                                    isPagination={false}
                                    setCommonFilterModalOpen={setCommonFilterModalOpen}
                                    pageName={"ConversationTemplate"}
                                />
                            ) : (
                                <Loaders />
                            )}
                        </CardBody>
                    </Card>

                    <AllCommonFilterModal
                        setCommonFilterModalOpen={setCommonFilterModalOpen}
                        commonFilterModalOpen={commonFilterModalOpen}
                        modalTitle="Conversation Template"
                        handleSubmit={handleFilterSubmit}
                        filterData={conversationTemplateContextData?.filters}
                    />
                </Container>
            </div>
        </React.Fragment>
    );
};

ConversationTemplate.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(ConversationTemplate);
